import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../query';
import { useComplianceApi } from '../api';
import { compKeys } from '../query-key-factory';
import {
  AccountCodeCountResponse,
  AccountCodeResponse,
  AccountCodesListResponse,
} from './types/accountCodes.types';
import { AccountCodesSearch, GetAccountCodeMeta, GetAccountCodesMeta } from './types/search.types';

const useGetAccountCodes = () => {
  const ComplianceApi = useComplianceApi();

  const getAccountCodes = async (context: {
    meta?: GetAccountCodesMeta;
  }): Promise<AccountCodesListResponse> => {
    const { queryString } = context.meta || {};
    const { data } = await ComplianceApi.get<AccountCodesListResponse>(
      `/account_codes?${queryString}`
    );

    return data;
  };

  return getAccountCodes;
};

const useListAccountCodes = <TResult = AccountCodesListResponse>({
  pagination,
  queryOptions,
  causeId,
  _enabled = true,
  select,
}: {
  pagination?: QueryPagination;
  queryOptions?: AccountCodesSearch;
  causeId: string;
  _enabled?: boolean;
  select?: (data: AccountCodesListResponse) => TResult;
}) => {
  const causeQuery = `cause_id=${causeId}`;
  const getAccountCodes = useGetAccountCodes();
  const { queryString, queryObject, queryPagination } = createQueryOptions<AccountCodesSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: causeQuery,
  });

  const queryKey = causeId ? compKeys.id(causeId).accountCodes.all : [];
  return useQuery({
    queryKey: [...queryKey, queryObject, queryPagination],
    queryFn: getAccountCodes,
    meta: { queryString, causeId },
    placeholderData: keepPreviousData,
    enabled: !!causeId && _enabled,
    select,
  });
};

const useGetAccountCode = () => {
  const ComplianceApi = useComplianceApi();
  const getAccountCode = async (context: { meta?: GetAccountCodeMeta }) => {
    const { codeId, causeId } = context.meta || {};
    const { data } = await ComplianceApi.get<AccountCodeResponse>(
      `/account_codes/${codeId}?cause_id=${causeId}`
    );
    return data;
  };

  return getAccountCode;
};

const useShowAccountCodeById = ({ causeId, codeId }: { causeId: string; codeId?: string }) => {
  const getAccountCode = useGetAccountCode();

  const key = codeId ? compKeys.id(causeId).accountCodes.id(codeId).detail : [];

  return useQuery({
    queryKey: key,
    queryFn: getAccountCode,
    meta: { codeId, causeId },
    enabled: !!codeId && !!causeId,
  });
};

const useShowAccountCodeCounts = ({
  accountCodeUuid,
  causeId,
}: {
  accountCodeUuid?: string;
  causeId?: string;
}) => {
  const ComplianceApi = useComplianceApi();

  const getAccountCodeCounts = async () => {
    const { data } = await ComplianceApi.get<AccountCodeCountResponse>(
      `/account_codes/${accountCodeUuid}/count?cause_id=${causeId}`
    );

    return {
      ...data,
      total_by_type: {
        storeProducts: data?.total_by_type?.['StoreItems::Item'] ?? 0,
        campaigns: data?.total_by_type?.['StoreItems::Campaign'] ?? 0,
        athons: data?.total_by_type?.['StoreItems::Athon'] ?? 0,
        directDonations: data?.total_by_type?.['StoreItems::Donation'] ?? 0,
        posQuickPay: data?.total_by_type?.['StoreItems::Pos'] ?? 0,
        productFundraisers: data?.total_by_type?.['StoreItems::ProductFundraiser'] ?? 0,
        memberships: data?.total_by_type?.['StoreItems::Membership'] ?? 0,
      },
    };
  };

  const queryKey =
    causeId && accountCodeUuid ? compKeys.id(causeId).accountCodes.id(accountCodeUuid).count : [];

  return useQuery({
    queryKey,
    queryFn: getAccountCodeCounts,
    enabled: !!causeId && !!accountCodeUuid,
  });
};

const useShowAccountCodeByCode = ({
  causeId,
  accountCode,
}: {
  causeId: string;
  accountCode?: string | null;
}) => {
  const ComplianceApi = useComplianceApi();
  const getAccountCodeByCode = async () => {
    const { data } = await ComplianceApi.get<AccountCodeResponse>(
      `/account_codes/code?cause_id=${causeId}&code=${accountCode}`
    );
    return data.account_code;
  };

  const queryKey = accountCode ? compKeys.id(causeId).accountCodes.id(accountCode).detail : [];

  return useQuery({
    queryKey,
    queryFn: getAccountCodeByCode,
    enabled: !!accountCode,
  });
};

export {
  useGetAccountCodes,
  useListAccountCodes,
  useShowAccountCodeByCode,
  useShowAccountCodeById,
  useShowAccountCodeCounts,
};
